/**
 * Guarda rotas autenticadas. Se não houver session do Supabase, redireciona
 * pra /login. Se houver session mas o user precisa subir pra AAL2 (tem
 * factor 2FA verified mas a sessão atual é só AAL1), redireciona pra
 * /auth/mfa-challenge.
 *
 * Regra crítica: o useEffect roda UMA VEZ no mount. Se colocarmos `router`
 * na dep array, ele dispara em toda mudança de rota → status volta pra
 * "checking" → tela pisca branco em cada navegação.
 *
 * O onAuthStateChange já cobre logout em outras abas e refresh de token.
 *
 * Sobre MFA / AAL:
 *   - signInWithPassword sempre dá AAL1 (sem MFA)
 *   - Se user tem factor verified, AAL2 é exigido pra acessar áreas autenticadas
 *   - mfa.getAuthenticatorAssuranceLevel() retorna { currentLevel, nextLevel }
 *     - currentLevel='aal1' + nextLevel='aal2' = precisa challenge
 *     - currentLevel='aal2' = OK
 *     - currentLevel='aal1' + nextLevel='aal1' = sem MFA configurado, OK
 */
import { useEffect, useRef, useState, ReactNode } from "react";
import { useRouter } from "next/router";
import { getSupabase } from "@/lib/supabase";

type Status = "checking" | "authenticated" | "guest" | "needs_mfa";

export default function RequireAuth({ children }: { children: ReactNode }) {
  const router    = useRouter();
  const routerRef = useRef(router);
  routerRef.current = router;

  const [status, setStatus] = useState<Status>("checking");

  useEffect(() => {
    const supabase = getSupabase();
    let mounted = true;

    async function check() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;

      if (!session) {
        setStatus("guest");
        routerRef.current.replace("/login");
        return;
      }

      // Tem session — checar se precisa subir pra AAL2
      try {
        const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (!mounted) return;

        // Se nextLevel é 'aal2' e currentLevel é 'aal1', precisa challenge
        if (aal?.nextLevel === "aal2" && aal?.currentLevel === "aal1") {
          setStatus("needs_mfa");
          routerRef.current.replace("/auth/mfa-challenge");
          return;
        }

        setStatus("authenticated");
      } catch {
        // Se falhar, libera mesmo assim (não bloquear user por erro de check)
        setStatus("authenticated");
      }
    }

    check();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (!session) {
        setStatus("guest");
        routerRef.current.replace("/login");
      } else {
        // Re-check AAL após login/refresh
        check();
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "checking" || status === "needs_mfa") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-7 h-7 border-2 border-stone-200 border-t-stone-900 rounded-full animate-spin" />
      </div>
    );
  }

  if (status === "guest") return null;

  return <>{children}</>;
}
